const API = process.env.REACT_APP_API_URL

/**
 * The raw subscription document as returned by `GET /subscription/get`.
 * Only the fields the frontend reads are typed; the rest is passed through.
 */
export interface Subscription {
  isTrial?: boolean
  credit?: number
  trialPointsLimit?: number
  trialDaysLimit?: number
  type?: string
  period?: string
  subscribed?: boolean
  paymentCustomerId?: string
  paymentSubscriptionId?: string
  serviceUsage?: Record<string, number>
  [key: string]: unknown
}

let inFlightSubscription: Promise<Subscription> | null = null

/**
 * Fetches the current subscription. Concurrent callers share one in-flight
 * request, so a screen with several credit widgets (or React 18 StrictMode
 * double-invoking effects) still hits the API once. Owned by `SubscriptionProvider`
 * — components should read it through `useSubscription()`, not call this directly.
 */
export async function getSubscription(): Promise<Subscription> {
  if (inFlightSubscription) return inFlightSubscription

  inFlightSubscription = fetch(`${API}/subscription/get`, { credentials: 'include' })
    .then(res => {
      if (!res.ok) throw new Error(`subscription/get responded ${res.status}`)
      return res.json()
    })
    .finally(() => {
      inFlightSubscription = null
    })

  return inFlightSubscription
}

export interface SubscriptionChangeResult {
  ok?: boolean
  cancelAtPeriodEnd?: boolean
  currentPeriodEnd?: string | null
  message?: unknown
}

/**
 * Schedules the current subscription to end when the paid period runs out.
 * Credit and access stay available until then.
 */
export async function cancelSubscription(): Promise<SubscriptionChangeResult> {
  const res = await fetch(`${API}/stripe/subscription/cancel`, {
    method: 'POST',
    credentials: 'include',
  })
  return res.json()
}

/** Reverts a scheduled cancellation while the subscription is still running. */
export async function reactivateSubscription(): Promise<SubscriptionChangeResult> {
  const res = await fetch(`${API}/stripe/subscription/reactivate`, {
    method: 'POST',
    credentials: 'include',
  })
  return res.json()
}

export interface StartTrialResult {
  ok?: boolean
  type?: string
  daysLeft?: number
  pointsLeft?: number
  message?: unknown
}

// Opt-in trial from Billing: 25 points / 14 days, once per account.
export async function startTrial(): Promise<StartTrialResult> {
  const res = await fetch(`${API}/subscription/trial/start`, {
    method: 'POST',
    credentials: 'include',
  })
  return res.json()
}

export interface CreditCheckResult {
  ok?: boolean
  allowed?: boolean
  pointsLeft?: number
  cost?: number
  // Only set for QES: whether the authenticated user's first-use surcharge applies.
  firstUse?: boolean
  // Only set for QES: another first-use operation is unresolved, so the price is not
  // final and confirmation must not proceed on a misleading amount.
  qesPending?: boolean
  // Only set for QES: the active subscription set is ambiguous, so the surcharge
  // cannot be determined at all.
  qesUnavailable?: boolean
}

// Pre-flight credit check; service keys match the API's getServicePoints.
export async function checkCredit(service: string, count?: number): Promise<CreditCheckResult> {
  const res = await fetch(`${API}/subscription/check/credit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ service, number: count }),
  })
  return res.json()
}

export interface ServiceCosts {
  schufaOne: number
  schufaMany: number
  identity: number
  transparenzregister: number
  handelsregister: number
  amlSearch: number
  'aml-monitoring': number
  'handelsregister-ch': number
  'transparenzregister-ch': number
  uid: number
  stammdaten: number
  // Topograph cost is dynamic per document, so the server sends a "min-max" range
  // string (e.g. "3.5–12.5") rather than a single number.
  topograph?: string
}

export async function fetchServiceCosts(): Promise<ServiceCosts | null> {
  try {
    const res = await fetch(`${API}/subscription/service-costs`, { credentials: 'include' })
    if (!res.ok) return null
    return await res.json()
  } catch {
    return null
  }
}

export async function fetchAmlSearchCountThisMonth(): Promise<number | null> {
  try {
    const res = await fetch(`${API}/subscription/service-usage/aml-search/count`, {
      credentials: 'include',
    })
    if (!res.ok) return null
    const data = await res.json()
    return data?.ok ? data.count : null
  } catch {
    return null
  }
}

export interface ServiceUsageDistributionEntry {
  service: string
  count: number
}

export interface ServiceUsageLog {
  _id?: string
  subdomain: string
  service: string
  userEmail?: string
  userName?: string
  userId?: string
  usedAt: string
  pointsDeducted?: number
  meta?: Record<string, unknown>
}

export async function fetchServiceUsageDistribution(
  month?: string
): Promise<ServiceUsageDistributionEntry[] | null> {
  try {
    const query = month ? `?month=${encodeURIComponent(month)}` : ''
    const res = await fetch(`${API}/subscription/service-usage/distribution${query}`, {
      credentials: 'include',
    })
    if (!res.ok) return null
    const data = await res.json()
    return data?.ok && Array.isArray(data.distribution) ? data.distribution : null
  } catch {
    return null
  }
}

export async function fetchServiceUsageHistory(
  limit: number = 10
): Promise<ServiceUsageLog[] | null> {
  try {
    const res = await fetch(`${API}/subscription/service-usage/history`, { credentials: 'include' })
    if (!res.ok) return null
    const data = await res.json()
    return Array.isArray(data) ? data.slice(0, limit) : null
  } catch {
    return null
  }
}
